import { useQuery } from '@apollo/client'
import { useMemo } from 'react'
import type { ComboboxCommonProps, ComboboxItem } from '@/components'
import { Combobox } from '@/components'
import type { RelayNodeType } from '@/types/graphql'
import { useOnErrorAuto } from '@/utils/hooks'
import { ALL_ISSUE_CATEGORIES_SHALLOW_QUERY } from '../queries'
import type {
  AllIssueCategoriesShallowQuery,
  AllIssueCategoriesShallowQueryVariables,
} from '../types.graphl'

export type ShallowCategory = RelayNodeType<
  AllIssueCategoriesShallowQuery<true>['allIssueCategories']
>

interface CategorySelectProps extends ComboboxCommonProps<string> {
  value: string | null
  error?: string | null
  onChange: (value: string, category: ShallowCategory | null) => void
}

export const CategorySelect: React.FC<CategorySelectProps> = ({
  value,
  error,
  onChange,

  ...comboboxProps
}) => {
  const onErrorAuto = useOnErrorAuto()

  const { data } = useQuery<
    AllIssueCategoriesShallowQuery<true>,
    AllIssueCategoriesShallowQueryVariables
  >(ALL_ISSUE_CATEGORIES_SHALLOW_QUERY, {
    variables: {
      withResponsibleUser: true,
    },
    onError: onErrorAuto(),
  })

  const categories = useMemo(
    () => data?.allIssueCategories.edges.map(({ node }) => node) ?? [],
    [data]
  )

  const items = useMemo<ComboboxItem<string>[]>(
    () =>
      categories.map(category => ({
        value: category.id,
        label: category.name,
      })),
    [categories]
  )

  return (
    <div>
      <Combobox
        {...comboboxProps}
        mode="single"
        value={value ?? ''}
        items={items}
        onChange={id =>
          onChange(id, categories.find(category => category.id === id) ?? null)
        }
      />
      {error && <p className="mt-1 text-xs text-rose-500">{error}</p>}
    </div>
  )
}
